import type { FrameTimingSource } from './streamRecovery/contracts';
import { hasValidFrameObservation, withoutInvalidFrameObservation } from './streamRecovery/validators';

/**
 * Wire shapes for the calibrated glass-to-glass latency probe.
 *
 * The rVFC hint path only exists on Chromium guests (Safari's rVFC exposes no
 * rtpTimestamp), so a datachannel handshake is the only way to get a calibrated
 * latency measurement on Safari. The probe is a tiny NTP-style round trip over the
 * existing 'data' channel: the host pings with its wall time, the guest pongs back
 * the echo plus its own wall time, and the host estimates the clock offset from the
 * round trip. Once the offset is known, a guest's decode timestamp can be converted
 * into host time and a real end-to-end latency computed for any correlated frame.
 *
 * Every guest→host report MUST carry an explicit `source`. There is no silent fallback:
 * missing or unknown sources fail the guard so the host never treats untagged data as
 * authoritative.
 */

export const LATENCY_PROBE_MESSAGE_TYPE = 'latencyProbe' as const;

/**
 * How the guest produced a latency report.
 * - `rvfc-rtp`: Chromium rVFC `rtpTimestamp` (media RTP timestamp).
 * - `rvfc-capture-time`: WebKit rVFC `captureTime`/`receiveTime` when populated.
 * - `estimate`: RTT-anchored estimate — debug/log only; never feeds the climb gate.
 */
export type LatencyProbeSource = 'rvfc-rtp' | 'rvfc-capture-time' | 'estimate';

const LATENCY_PROBE_SOURCES: ReadonlySet<string> = new Set([
  'rvfc-rtp',
  'rvfc-capture-time',
  'estimate',
]);

export function isLatencyProbeSource(value: unknown): value is LatencyProbeSource {
  return typeof value === 'string' && LATENCY_PROBE_SOURCES.has(value);
}

/** Host -> guest: begin one offset round trip. */
export type LatencyProbePing = {
  type: typeof LATENCY_PROBE_MESSAGE_TYPE;
  role: 'ping';
  probeId: number;
  /** Host local wall time (ms) when this ping was sent. */
  hostWallMs: number;
};

/** Guest -> host: answer a ping with the echo + the guest's wall time. */
export type LatencyProbePong = {
  type: typeof LATENCY_PROBE_MESSAGE_TYPE;
  role: 'pong';
  probeId: number;
  /** Echoed from the ping. */
  hostWallMs: number;
  /** Guest local wall time (ms) when this pong was assembled. */
  guestWallMs: number;
};

/** Guest -> host: a frame decode report for the calibrated latency measurement. */
export type LatencyProbeReport = {
  observationSequence?: number;
  observationAgeMs?: number;
  pathEpoch?: number;
  timingSource?: FrameTimingSource;
  type: typeof LATENCY_PROBE_MESSAGE_TYPE;
  role: 'report';
  /** Monotonic report sequence from the guest producer. */
  probeId: number;
  /**
   * Media RTP timestamp of the decoded frame (from rVFC `rtpTimestamp` on
   * Chromium, or from a Safari strategy that still keys the same field).
   * MUST NOT be `inbound-rtp.timestamp` (stats collection time).
   */
  rtpTimestamp: number;
  /** Composition-submission wall time for presentation-time reports; legacy callback time otherwise. */
  guestDecodeWallMs: number;
  /** How the guest produced this report. Required; no silent fallback. */
  source: LatencyProbeSource;
};

export type LatencyProbeMessage = LatencyProbePing | LatencyProbePong | LatencyProbeReport;

/** Structural guard for the probe messages. */
export function isLatencyProbeMessage(value: unknown): value is LatencyProbeMessage {
  if (value === null || typeof value !== 'object') return false;
  const msg = value as Record<string, unknown>;
  if (msg['type'] !== LATENCY_PROBE_MESSAGE_TYPE) return false;

  const role = msg['role'];
  const probeId = msg['probeId'];
  if (role !== 'ping' && role !== 'pong' && role !== 'report') return false;
  if (typeof probeId !== 'number' || !Number.isFinite(probeId)) return false;

  const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

  if (role === 'ping') {
    return num(msg['hostWallMs']);
  }
  if (role === 'pong') {
    return num(msg['hostWallMs']) && num(msg['guestWallMs']);
  }
  return (
    num(msg['rtpTimestamp']) &&
    num(msg['guestDecodeWallMs']) &&
    isLatencyProbeSource(msg['source']) && hasValidFrameObservation(msg)
  );
}

/** Preserve a valid legacy report when its optional observation extension is unsupported. */
export function parseLatencyProbeMessage(value: unknown): LatencyProbeMessage | undefined {
  if (isLatencyProbeMessage(value)) return value;
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const cleaned = withoutInvalidFrameObservation(value as Record<string, unknown>);
  return isLatencyProbeMessage(cleaned) ? cleaned : undefined;
}
